import { Alert, Button, FileInput, Modal, Stack, Textarea } from '@mantine/core';
import { IconCheck, IconFileSpreadsheet, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from '../../shell-context.js';
import { ImportPreview } from '../pms/ImportPreview.js';
import classes from './Compliance.module.css';
import { useCertificateErrorText } from './errors.js';
import type { CertificateListItem, ClassItemImportReport } from './types.js';

/**
 * "Add Remark" (ofis; ADR 0029 D13): sunucu satırı gemi günü ve kişiyle ekler; önceki notlar
 * değişmez, geçmiş denetim kaydındadır.
 */
export function AddRemarkDialog({
  opened,
  certificate,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  certificate: Pick<CertificateListItem, 'name'> | null;
  onClose: () => void;
  onSubmit: (text: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.dialogs.remark.${key}`);
  const errorText = useCertificateErrorText();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useUnsavedChanges(opened && text.trim() !== '');
  useEffect(() => {
    if (opened) {
      setText('');
      setError(null);
    }
  }, [opened]);
  const empty = text.trim() === '';
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.drawerTitle}>{k('title')}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          if (empty) {
            setError({ code: 'REQUIRED' });
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await onSubmit(text.trim());
          } catch (failure) {
            setError(failure);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Stack gap="sm">
          {certificate ? <p className={classes.muted}>{certificate.name}</p> : null}
          <Textarea
            label={k('text')}
            required
            autosize
            minRows={3}
            maxLength={2000}
            value={text}
            onChange={(event) => setText(event.currentTarget.value)}
            error={
              (error as { code?: string } | null)?.code === 'REQUIRED'
                ? t('vesselCertificates.fieldErrors.REQUIRED')
                : undefined
            }
          />
          <p className={classes.notice}>
            <IconInfoCircle size={16} aria-hidden />
            {k('hint')}
          </p>
        </Stack>
        {error && (error as { code?: string }).code !== 'REQUIRED' ? (
          <p className={classes.formError} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('vesselCertificates.dialogs.cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "Import class item list" (ofis; NR467 App 1 2.4.1; ADR 0029 D11): CSV ya da XLSX; önce satır
 * numaralı önizleme raporu, hatalı satır varsa hiçbir şey yazılmaz; dosyada olmayan kalem silinmez.
 */
export function ClassItemImportDialog({
  opened,
  onClose,
  onImport,
  onApplied,
}: {
  opened: boolean;
  onClose: () => void;
  onImport: (file: File, apply: boolean) => Promise<ClassItemImportReport>;
  onApplied: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.dialogs.import.${key}`, values);
  const errorText = useCertificateErrorText();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ClassItemImportReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (opened) {
      setFile(null);
      setReport(null);
      setError(null);
    }
  }, [opened]);

  const run = async (apply: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onImport(file, apply);
      setReport(result);
      if (apply && result.applied) onApplied();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  const errorsOf = report?.errors ?? [];
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={<span className={classes.drawerTitle}>{k('title')}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      <Stack gap="sm">
        <p className={classes.muted}>{k('hint')}</p>
        <FileInput
          label={k('file')}
          placeholder={k('choose')}
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          leftSection={<IconFileSpreadsheet size={16} aria-hidden />}
          value={file}
          clearable
          onChange={(value) => {
            setFile(value);
            setReport(null);
          }}
        />
        {report ? (
          <>
            {report.applied ? (
              <Alert color="green" icon={<IconCheck size={16} />} role="status">
                {k('applied', { added: report.added.length, updated: report.updated.length })}
              </Alert>
            ) : null}
            <ClassItemImportReportView report={report} />
          </>
        ) : null}
        {error ? (
          <p className={classes.formError} role="alert">
            {errorText(error)}
          </p>
        ) : null}
      </Stack>
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {report?.applied
            ? t('vesselCertificates.dialogs.close')
            : t('vesselCertificates.dialogs.cancel')}
        </Button>
        <Button variant="default" disabled={!file || busy} onClick={() => void run(false)}>
          {k('preview')}
        </Button>
        <Button
          loading={busy}
          disabled={!file || !report || report.applied || errorsOf.length > 0}
          onClick={() => void run(true)}
        >
          {k('apply')}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Önizleme raporu (ADR 0029 D11, Ek E10): M7c'nin ortak `ImportPreview` bileşeni (özet kutuları,
 * sıralanan ve sayfalanan satır numaralı hata tablosu); hata yoksa eklenecek kalemler.
 */
export function ClassItemImportReportView({ report }: { report: ClassItemImportReport }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.dialogs.import.${key}`, values);
  const errors = report.errors;
  return (
    <div aria-live="polite">
      <ImportPreview
        tiles={[
          { key: 'rows', label: k('tiles.rows'), value: report.rows },
          { key: 'added', label: k('tiles.added'), value: report.added.length },
          { key: 'updated', label: k('tiles.updated'), value: report.updated.length },
          { key: 'unchanged', label: k('tiles.unchanged'), value: report.unchanged },
          ...(errors.length > 0
            ? [
                {
                  key: 'errors',
                  label: k('tiles.errors'),
                  value: new Set(errors.map((e) => e.row)).size,
                  tone: 'danger' as const,
                },
              ]
            : []),
        ]}
        errors={errors.map((e) => ({
          row: e.row,
          code: e.code,
          ...(e.column ? { column: e.column } : {}),
        }))}
        warnings={[]}
        codeText={(code) => t(`vesselCertificates.errors.${code}`, { defaultValue: code })}
      >
        {errors.length > 0 ? (
          <Alert color="red" role="alert">
            {k('errorsTitle')}
          </Alert>
        ) : report.added.length > 0 ? (
          <ReportTable
            label={k('added', { count: report.added.length })}
            columns={[k('addedColumns.row'), k('addedColumns.code'), k('addedColumns.description')]}
            rows={report.added.map((a) => [String(a.row), a.itemCode, a.description])}
          />
        ) : null}
        {report.missing.length > 0 ? (
          <p className={classes.muted}>{k('missing', { codes: report.missing.join(', ') })}</p>
        ) : null}
      </ImportPreview>
    </div>
  );
}

function ReportTable({
  label,
  columns,
  rows,
}: {
  label: string;
  columns: string[];
  rows: string[][];
}) {
  return (
    <section
      className={classes.tableBox}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
      style={{ maxHeight: 260, overflowY: 'auto' }}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('|')}>
              {row.map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: sabit sütunlar
                <td key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
